import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import {
  Heart, HeartPulse, Bone, Brain, Baby, Scissors, Activity, Leaf, Sparkles, Stethoscope,
  Eye, Smile, Scale, ArrowRight, CheckCircle, Building2, Clock, DollarSign
} from "lucide-react";

const data = {
  "modern-medicine": {
    title: "Modern Medicine",
    subtitle: "Advanced treatments at world-class hospitals",
    desc: "Access cutting-edge medical procedures performed by internationally trained specialists at Kerala's top accredited hospitals. From minimally invasive cardiac surgery to robotic-assisted joint replacements, India offers the latest in medical technology at a fraction of Western costs.",
    icon: Stethoscope,
    color: "bg-blue-50 text-blue-600",
    heroColor: "from-[#03045e] to-[#00A4A9]",
    heroImage: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxob3NwaXRhbCUyMGNvb3JkaW5hdGlvbiUyMGRvY3RvcnMlMjB0ZWFtJTIwbWVldGluZ3xlbnwwfHx8fDE3NzYzMTQ1NTR8MA&ixlib=rb-4.1.0&q=85",
    accentImages: [
      "https://images.unsplash.com/photo-1758691462119-792279713969?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjBwYXRpZW50JTIwY29uc3VsdGF0aW9uJTIwaG9zcGl0YWx8ZW58MHx8fHwxNzc2MjUyMjY4fDA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1758691462814-485c3672e447?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwdHJlYXRtZW50JTIwcGxhbm5pbmclMjBjbGlwYm9hcmQlMjBkb2N0b3J8ZW58MHx8fHwxNzc2MzE0NTY1fDA&ixlib=rb-4.1.0&q=85"
    ],
    treatments: [
      { icon: Heart, name: "Cardiology", desc: "Heart bypass, valve replacement, angioplasty, pacemaker implantation", cost: "$3,000 - $7,000", recovery: "5-14 days hospital" },
      { icon: Bone, name: "Orthopedics", desc: "Total knee/hip replacement, spine surgery, arthroscopy, ACL reconstruction", cost: "$4,000 - $8,000", recovery: "3-7 days hospital" },
      { icon: Activity, name: "Oncology", desc: "Surgical oncology, chemotherapy, radiation (IMRT, CyberKnife), immunotherapy", cost: "$5,000 - $25,000", recovery: "Varies by treatment" },
      { icon: Brain, name: "Neurology", desc: "Brain tumor surgery, deep brain stimulation, epilepsy surgery, stroke treatment", cost: "$5,000 - $15,000", recovery: "7-21 days hospital" },
    ],
    hospitals: ["KIMS Hospital", "Aster Medcity", "Amrita Hospital", "Lakeshore Hospital"],
    keywords: "modern medicine India, cardiology Kerala, orthopedics India, cancer treatment Kerala"
  },
  "ayurveda": {
    title: "Ayurveda",
    subtitle: "Authentic traditional healing from Kerala",
    desc: "Kerala is the birthplace of Ayurveda — a holistic healing system with over 5,000 years of tradition. Our partner institutions offer authentic Panchakarma detox programs, therapeutic oil treatments, and personalized wellness protocols supervised by experienced Ayurvedic physicians (Vaidyas).",
    icon: Leaf,
    color: "bg-green-50 text-green-700",
    heroColor: "from-[#065f46] to-[#059669]",
    heroImage: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accentImages: [
      "https://images.unsplash.com/photo-1545205597-3d9d02c29597?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      "https://images.unsplash.com/photo-1591343395082-e120087004b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
    ],
    treatments: [
      { icon: Leaf, name: "Panchakarma Detox", desc: "Complete 14-21 day body purification therapy: Vamana, Virechana, Basti, Nasya, Raktamoksha", cost: "$1,000 - $2,500", recovery: "21-28 day program" },
      { icon: Leaf, name: "Abhyanga & Oil Therapy", desc: "Full-body synchronized oil massage for pain relief, stress reduction, and rejuvenation", cost: "$500 - $1,200", recovery: "7-14 day program" },
      { icon: Leaf, name: "Shirodhara", desc: "Continuous warm oil flow on the forehead for neurological disorders, insomnia, anxiety", cost: "$400 - $800", recovery: "7-14 sessions" },
      { icon: Leaf, name: "Pizhichil (Oil Bath)", desc: "Warm medicated oil poured over the body for arthritis, paralysis, neurological conditions", cost: "$800 - $1,500", recovery: "14-21 day program" },
      { icon: Leaf, name: "Njavarakizhi", desc: "Rice bolus massage for muscle wasting, joint stiffness, and skin conditions", cost: "$600 - $1,200", recovery: "14 day program" },
      { icon: Leaf, name: "Rasayana (Rejuvenation)", desc: "Anti-aging and immunity-boosting therapies with herbal medicines and lifestyle modifications", cost: "$800 - $2,000", recovery: "21-28 day program" },
    ],
    hospitals: ["Kottakkal Arya Vaidya Sala", "Somatheeram Ayurveda", "Vaidyaratnam", "Dhathri Ayurveda"],
    keywords: "Ayurveda Kerala, Panchakarma India, authentic Ayurvedic treatment, Shirodhara Kerala"
  },
  "wellness": {
    title: "Wellness",
    subtitle: "Preventive care and holistic well-being",
    desc: "Kerala's natural beauty combined with advanced medical facilities creates the perfect setting for wellness retreats. Our programs combine modern diagnostics with traditional therapies, yoga, meditation, and nutrition counseling for complete mind-body rejuvenation.",
    icon: Sparkles,
    color: "bg-purple-50 text-purple-600",
    heroColor: "from-[#4c1d95] to-[#7c3aed]",
    heroImage: "https://images.unsplash.com/photo-1545389336-cf090694435e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accentImages: [
      "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      "https://images.unsplash.com/photo-1540206395-68808572332f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
    ],
    treatments: [
      { icon: Sparkles, name: "Executive Health Checkup", desc: "Comprehensive 2-day health screening with advanced diagnostics, blood work, imaging", cost: "$300 - $800", recovery: "1-2 days" },
      { icon: Sparkles, name: "Lifestyle Detox Program", desc: "7-14 day detox combining Ayurvedic cleansing, yoga, organic diet, and counseling", cost: "$1,000 - $3,000", recovery: "7-14 days" },
      { icon: Sparkles, name: "Yoga & Meditation Retreat", desc: "Structured yoga therapy, pranayama, meditation, and mindfulness programs", cost: "$500 - $1,500", recovery: "7-21 days" },
      { icon: Sparkles, name: "Stress Management", desc: "Customized programs for burnout, anxiety, insomnia combining therapy and Ayurveda", cost: "$800 - $2,000", recovery: "14-21 days" },
      { icon: Sparkles, name: "Weight Management", desc: "Medically supervised weight loss with diet plans, exercise, and optional bariatric consultation", cost: "$600 - $2,500", recovery: "14-28 days" },
      { icon: Sparkles, name: "Anti-Aging Programs", desc: "Skin rejuvenation, Rasayana therapies, hormone assessment, and lifestyle optimization", cost: "$1,000 - $3,000", recovery: "14-21 days" },
    ],
    hospitals: ["KIMS Wellness Center", "Somatheeram", "Aster Wellness", "Lakeshore Wellness"],
    keywords: "wellness retreat Kerala, health checkup India, yoga retreat Kerala, detox program India"
  },
  "rehabilitation": {
    title: "Rehabilitation",
    subtitle: "Recovery and restoration of function",
    desc: "Our partner hospitals offer comprehensive rehabilitation services combining modern physiotherapy with traditional Ayurvedic recovery methods. Expert teams help patients regain mobility, strength, and independence after surgery, injury, or neurological events.",
    icon: Activity,
    color: "bg-amber-50 text-amber-600",
    heroColor: "from-[#92400e] to-[#d97706]",
    heroImage: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accentImages: [
      "https://images.unsplash.com/photo-1741707039536-113e200f9e0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxwYXRpZW50JTIwcmVjb3ZlcnklMjBjYXJlJTIwbnVyc2UlMjBzbWlsaW5nfGVufDB8fHx8MTc3NjMxNDU3MHww&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1519824145371-296894a0daa9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
    ],
    treatments: [
      { icon: Activity, name: "Post-Surgical Rehab", desc: "Recovery after joint replacement, spine surgery, or cardiac procedures with physiotherapy", cost: "$1,000 - $3,000", recovery: "2-6 weeks" },
      { icon: Activity, name: "Neurological Rehab", desc: "Recovery from stroke, brain injury, spinal cord injury with multidisciplinary team", cost: "$2,000 - $5,000", recovery: "4-12 weeks" },
      { icon: Activity, name: "Sports Rehab", desc: "Injury recovery for athletes: ACL, shoulder, ankle with sports-specific training", cost: "$1,500 - $4,000", recovery: "4-8 weeks" },
      { icon: Activity, name: "Cardiac Rehab", desc: "Supervised exercise, lifestyle counseling after heart surgery or cardiac events", cost: "$1,000 - $2,500", recovery: "4-8 weeks" },
      { icon: Activity, name: "Ayurvedic Rehabilitation", desc: "Traditional therapies for chronic pain, arthritis, paralysis combining Ayurveda and physiotherapy", cost: "$800 - $2,000", recovery: "3-6 weeks" },
      { icon: Activity, name: "Pediatric Rehab", desc: "Developmental therapy for children with cerebral palsy, autism, developmental delays", cost: "$1,500 - $4,000", recovery: "4-12 weeks" },
    ],
    hospitals: ["KIMS Rehabilitation", "Amrita Hospital", "Baby Memorial Hospital", "Aster Medcity"],
    keywords: "rehabilitation India, physiotherapy Kerala, post-surgery recovery India, neurological rehab"
  },
  "specialty-clinics": {
    title: "Specialty Clinics",
    subtitle: "Focused expertise in niche medical areas",
    desc: "Access specialized clinics dedicated to specific conditions and treatments. These focused centers offer deep expertise, advanced equipment, and streamlined protocols for optimal outcomes in their respective fields.",
    icon: Heart,
    color: "bg-pink-50 text-pink-600",
    heroColor: "from-[#9d174d] to-[#ec4899]",
    heroImage: "https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accentImages: [
      "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
    ],
    treatments: [
      { icon: Baby, name: "IVF & Fertility Center", desc: "In vitro fertilization, ICSI, egg/sperm freezing, donor programs, surrogacy counseling", cost: "$2,500 - $5,000", recovery: "2-3 weeks per cycle" },
      { icon: Scissors, name: "Cosmetic Surgery", desc: "Rhinoplasty, liposuction, breast augmentation, facelift, hair transplant", cost: "$1,500 - $6,000", recovery: "1-3 days hospital" },
      { icon: Smile, name: "Dental Implants & Care", desc: "Full mouth rehabilitation, dental implants, veneers, root canal, cosmetic dentistry", cost: "$500 - $3,000", recovery: "3-7 days" },
      { icon: Eye, name: "Eye Care & LASIK", desc: "LASIK surgery, cataract removal, glaucoma treatment, retinal surgery", cost: "$500 - $2,000", recovery: "1-3 days" },
      { icon: Scale, name: "Bariatric Surgery", desc: "Gastric bypass, sleeve gastrectomy, gastric banding for medically supervised weight loss", cost: "$3,000 - $6,000", recovery: "5-7 days hospital" },
      { icon: Heart, name: "Dermatology & Skin", desc: "Skin disease treatment, laser therapy, vitiligo treatment, psoriasis management", cost: "$300 - $2,000", recovery: "Varies" },
      { icon: Stethoscope, name: "ENT & Audiology", desc: "Sinus surgery, tonsillectomy, cochlear implants, hearing aid fitting", cost: "$1,000 - $4,000", recovery: "3-7 days" },
    ],
    hospitals: ["Lakeshore Hospital", "KIMS Hospital", "Amrita Hospital", "Baby Memorial Hospital"],
    keywords: "IVF India, dental implants Kerala, LASIK India, bariatric surgery Kerala, specialty hospital"
  },
  "integrative-medicine": {
    title: "Integrative Medicine",
    subtitle: "Modern medicine combined with Ayurveda, yoga and nutrition",
    desc: "Integrative Medicine blends the precision of modern allopathic care with time-tested traditions like Ayurveda, yoga and clinical nutrition — giving you the best of both worlds. Kerala's leading hospitals are pioneers in this approach, offering personalized programs that treat the whole person, not just the condition.",
    icon: HeartPulse,
    color: "bg-teal-50 text-teal-700",
    heroColor: "from-[#0f766e] to-[#14b8a6]",
    heroImage: "https://images.unsplash.com/photo-1545389336-cf090694435e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    accentImages: [
      "https://images.unsplash.com/photo-1600334129128-685c5582fd35?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
    ],
    treatments: [
      { icon: HeartPulse, name: "Cardio-Ayurveda Programs", desc: "Combined cardiology + lifestyle, nutrition and Ayurvedic protocols for heart health", cost: "$1,500 - $4,500", recovery: "2-4 weeks program" },
      { icon: Brain, name: "Mind-Body Wellness", desc: "Stress, anxiety and insomnia treated through psychiatry + Shirodhara + yoga therapy", cost: "$800 - $3,000", recovery: "14-28 days" },
      { icon: Activity, name: "Integrative Cancer Care", desc: "Modern oncology paired with supportive Ayurveda, nutrition and physiotherapy", cost: "Quote on consult", recovery: "Varies by stage" },
      { icon: Leaf, name: "Diabetes Reversal Program", desc: "Endocrinology + Panchakarma + diet to reduce dependence on medication", cost: "$1,200 - $3,500", recovery: "21-day intensive" },
      { icon: Bone, name: "Post-Surgical Recovery", desc: "Orthopaedic / cardiac post-op pairing physio with Abhyanga, marma therapy", cost: "$900 - $2,800", recovery: "2-6 weeks" },
      { icon: Sparkles, name: "Detox & Rejuvenation", desc: "Panchakarma supervised by both an Ayurvedic Vaidya and an internist", cost: "$1,000 - $2,500", recovery: "14-21 days" },
    ],
    hospitals: ["Amrita Hospital", "KIMS Hospital", "Kottakkal AVS", "Aster Medcity"],
    keywords: "integrative medicine Kerala, ayurveda allopathy combined, holistic medicine India, mind body wellness Kerala"
  }
};

export default function HealthcarePage() {
  const { category } = useParams();
  const page = data[category];

  if (!page) {
    return (
      <div className="section-padding text-center">
        <h2 className="font-outfit text-2xl text-[#03045e]">Category not found</h2>
        <Link to="/patients"><Button className="mt-4 rounded-full bg-[#00A4A9] text-white">Back to Home</Button></Link>
      </div>
    );
  }

  const Icon = page.icon;

  return (
    <div data-testid={`healthcare-${category}`}>
      <SEO title={page.title} description={page.desc} path={`/healthcare/${category}`} keywords={page.keywords} />

      {/* Hero */}
      <section className={`section-padding bg-gradient-to-br ${page.heroColor} text-white relative overflow-hidden`}>
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-white/5 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <Link to="/patients" className="inline-flex items-center text-sm text-white/70 hover:text-white mb-8" data-testid="back-link">
            <ArrowRight className="w-4 h-4 mr-1 rotate-180" /> Back to Home
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
            <FadeIn>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-white/20 text-white border-0 hover:bg-white/20 rounded-full">{page.treatments.length} Treatments</Badge>
              </div>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight mb-4">
                {page.title}
              </h1>
              <p className="text-xl text-white/80 mb-6">{page.subtitle}</p>
              <p className="text-base text-white/70 max-w-xl leading-relaxed mb-8">{page.desc}</p>
              <Link to="/contact" data-testid="healthcare-cta">
                <Button size="lg" className="rounded-full bg-white text-[#03045e] hover:bg-white/90 px-8 h-12 font-semibold">
                  Get Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </FadeIn>

            {page.heroImage && (
              <FadeIn delay={150}>
                <div className="relative">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/15" data-testid="healthcare-hero-photo-main">
                    <img src={page.heroImage} alt={page.title} className="w-full h-[320px] sm:h-[400px] lg:h-[460px] object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/30 via-transparent to-transparent" />
                  </div>
                  {page.accentImages && page.accentImages.length >= 2 && (
                    <div className="hidden sm:grid grid-cols-2 gap-3 mt-3">
                      <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-white/15" data-testid="healthcare-hero-photo-small-1">
                        <img src={page.accentImages[0]} alt={`${page.title} detail 1`} className="w-full h-32 object-cover" />
                      </div>
                      <div className="rounded-2xl overflow-hidden shadow-lg ring-1 ring-white/15" data-testid="healthcare-hero-photo-small-2">
                        <img src={page.accentImages[1]} alt={`${page.title} detail 2`} className="w-full h-32 object-cover" />
                      </div>
                    </div>
                  )}
                </div>
              </FadeIn>
            )}
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="overline">TREATMENTS</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3">Available Procedures</h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {page.treatments.map((tr, i) => {
              const TrIcon = tr.icon;
              return (
                <FadeIn key={tr.name} delay={i * 80}>
                  <div className="bg-white border border-slate-200 rounded-2xl p-7 card-hover shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`treatment-card-${i}`}>
                    <div className={`w-10 h-10 rounded-xl ${page.color} flex items-center justify-center mb-4`}>
                      <TrIcon className="w-5 h-5" />
                    </div>
                    <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-2">{tr.name}</h3>
                    <p className="text-sm text-slate-500 mb-4 leading-relaxed">{tr.desc}</p>
                    <div className="flex items-center justify-between text-xs border-t border-slate-100 pt-3">
                      <span className="flex items-center gap-1 text-[#00A4A9] font-medium"><DollarSign className="w-3.5 h-3.5" /> {tr.cost}</span>
                      <span className="flex items-center gap-1 text-slate-400"><Clock className="w-3.5 h-3.5" /> {tr.recovery}</span>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner Hospitals — hidden until partnerships are confirmed */}
      <section className="hidden">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-10">
              <span className="overline">HOSPITALS</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3">Our Partner Hospitals</h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {page.hospitals.map((h, i) => (
              <FadeIn key={h} delay={i * 60}>
                <div className="bg-white rounded-xl p-5 border border-slate-200 flex items-center gap-3 card-hover" data-testid={`hospital-${i}`}>
                  <Building2 className="w-8 h-8 text-[#00A4A9]/40 flex-shrink-0" />
                  <div>
                    <div className="font-outfit font-medium text-[#03045e] text-sm">{h}</div>
                    <div className="text-xs text-slate-400">Kerala, India</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Per-page CTA removed — sitewide PreFooterCTA handles this */}
    </div>
  );
}
